/***************************************************************
 * sortInspections.js
 *
 * Ranks NYC DOB inspection requests by urgency and returns
 * a sorted array (high priority first).
 ***************************************************************/

/**
 * SLA lookup for priority + taskType (Inspection vs. Re-Inspection).
 *
 * Priority + TaskType => SLA days from the Record Open Date
 */
const SLA_LOOKUP = {
  'B': {
    inspection: 40,
    reinspection: 150,
  },
  'B+': {
    inspection: 40,
    reinspection: 150,
  },
  'C': {
    inspection: 60,
    reinspection: 175,
  },
  'D': {
    inspection: 90,
    reinspection: 200,
  },
};

/**
 * Helper: compute integer day difference between two dates.
 * If dateA >= dateB, the result is how many whole days dateA is after dateB.
 */
function daysBetween(dateA, dateB) {
  const msPerDay = 24 * 60 * 60 * 1000;
  // e.g.  If dateA is today, dateB is the record open date,
  //       we get how many days have passed since open date.
  return Math.floor((dateA - dateB) / msPerDay);
}

/**
 * Returns the SLA (days) for a given priority & taskType
 */
function getSlaDays(priority, taskType) {
  const p = (priority || '').toUpperCase();    // e.g. 'B', 'B+', 'C', 'D'
  const t = (taskType || '').toLowerCase();    // 'inspection' or 'reinspection'

  if (SLA_LOOKUP[p] && SLA_LOOKUP[p][t] != null) {
    return SLA_LOOKUP[p][t];
  }
  return 0; // fallback
}

// Severity thresholds for each priority/taskType
const SEVERITY_THRESHOLDS = {
  'B': {
    inspection: [
      { label: 'Urgent', min: 30 },
      { label: 'Higher', min: 20 },
      { label: 'High', min: 10 },
      { label: 'Medium', min: 0 },
      { label: 'Low', min: 0 },
      { label: 'Lowest', min: 0 },
    ],
    reinspection: [
      { label: 'Higher', min: 150 },
      { label: 'High', min: 125 },
      { label: 'Medium', min: 100 },
      { label: 'Low', min: 75 },
      { label: 'Lowest', min: 0 },
    ],
  },
  'B+': {
    inspection: [
      { label: 'Urgent', min: 30 },
      { label: 'Higher', min: 20 },
      { label: 'High', min: 10 },
      { label: 'Medium', min: 0 },
      { label: 'Low', min: 0 },
      { label: 'Lowest', min: 0 },
    ],
    reinspection: [
      { label: 'Higher', min: 150 },
      { label: 'High', min: 125 },
      { label: 'Medium', min: 100 },
      { label: 'Low', min: 75 },
      { label: 'Lowest', min: 0 },
    ],
  },
  'C': {
    inspection: [
      { label: 'Urgent', min: 50 },
      { label: 'Higher', min: 40 },
      { label: 'High', min: 30 },
      { label: 'Medium', min: 20 },
      { label: 'Low', min: 10 },
      { label: 'Lowest', min: 0 },
    ],
    reinspection: [
      { label: 'Higher', min: 175 },
      { label: 'High', min: 150 },
      { label: 'Medium', min: 125 },
      { label: 'Low', min: 100 },
      { label: 'Lowest', min: 0 },
    ],
  },
  'D': {
    inspection: [
      { label: 'Urgent', min: 80 },
      { label: 'Higher', min: 70 },
      { label: 'High', min: 60 },
      { label: 'Medium', min: 50 },
      { label: 'Low', min: 40 },
      { label: 'Lowest', min: 30 },
    ],
    reinspection: [
      { label: 'Higher', min: 200 },
      { label: 'High', min: 175 },
      { label: 'Medium', min: 150 },
      { label: 'Low', min: 125 },
      { label: 'Lowest', min: 0 },
    ],
  },
};

// Helper to get severity bucket
function getSeverityBucket(priority, taskType, daysSinceOpen) {
  const p = (priority || '').toUpperCase();
  const t = (taskType || '').toLowerCase();
  const thresholds = SEVERITY_THRESHOLDS[p] && SEVERITY_THRESHOLDS[p][t];
  if (!thresholds) return 'Unknown';
  for (const threshold of thresholds) {
    if (daysSinceOpen >= threshold.min) {
      return threshold.label;
    }
  }
  return 'Lowest';
}

// Severity order for sorting (most severe first)
const SEVERITY_ORDER = ['Urgent', 'Higher', 'High', 'Medium', 'Low', 'Lowest', 'Unknown'];

/**
 * rankInspectionRequests
 *
 * - Determines daysLeft = SLA - daysSinceOpen
 * - Sorts ascending by daysLeft (lowest => highest priority)
 * - Optionally tie-breaks by location.
 * - Logs a summary array (priority, taskType, daysPassed) in sorted order.
 *
 * @param {Array} data - The array of objects read from XLSX, each like:
 *   {
 *     "INSPECTION_ID": "5826025",
 *     "Record_Open_Date": "2023-08-18 00:00:00",
 *     "Priority": "B",
 *     "TaskType": "Complaint Re-Inspection - QOL",
 *     "Status": "Scheduled",
 *     "Latitude": "40.643533",
 *     "Longitude": "-74.084529",
 *     ...
 *   }
 * @param {Object} options
 *   - locationTieBreak?: boolean
 *   - referenceLat?: number
 *   - referenceLng?: number
 *
 * @return {Array} sorted from high urgency (fewest daysLeft) to lower
 */
function rankInspectionRequests(data, options = {}) {
  const {
    locationTieBreak = false,
    referenceLat = 0,
    referenceLng = 0,
    selectedDate = new Date(),
  } = options;

  const today = new Date();

  // 1) Transform and filter
  const transformed = data
    .filter((row) => {
      // If it's a prescheduled inspection, check if it's for the selected date
      if (row.Status === "Scheduled") {
        if (!row.AppointmentStart) {
          return false; // Exclude prescheduled inspections without appointment start
        }
        
        // Parse the appointment date (format: "2025-04-16 10:00:00")
        const [appointmentDate] = row.AppointmentStart.split(' ');
        const [year, month, day] = appointmentDate.split('-').map(Number);
        
        // Compare with selected date components
        if (year === selectedDate.getFullYear() &&
            month === selectedDate.getMonth() + 1 && // JavaScript months are 0-based
            day === selectedDate.getDate()) {
          return true; // Include prescheduled inspections for the selected date
        }
        
        return false; // Exclude prescheduled inspections for other dates
      }
      
      return true; // Include all non-prescheduled inspections
    })
    .map((row) => {
      const openDate = new Date(row.Record_Open_Date);
      const daysSinceOpen = daysBetween(today, openDate);
      const priority = String(row.Priority || '').toUpperCase();
      let taskType = 'inspection';
      const taskTypeStr = (row.TaskType || '').toLowerCase();
      if (taskTypeStr.includes('re-inspection')) {
        taskType = 'reinspection';
      }
      const slaDays = getSlaDays(priority, taskType);
      const daysLeft = slaDays - daysSinceOpen;
      const lat = parseFloat(row.Latitude);
      const lng = parseFloat(row.Longitude);
      let tieBreakValue = 0;
      if (locationTieBreak && !Number.isNaN(lat) && !Number.isNaN(lng)) {
        const dx = lng - referenceLng;
        const dy = lat - referenceLat;
        tieBreakValue = Math.sqrt(dx * dx + dy * dy);
      }
      // Assign severity bucket
      const severityBucket = getSeverityBucket(priority, taskType, daysSinceOpen);
      return {
        ...row,
        openDate,
        daysSinceOpen,
        slaDays,
        daysLeft,
        lat,
        lng,
        taskType,
        priority,
        tieBreakValue,
        severityBucket,
      };
    });

  // 2) Sort by severity bucket, then daysLeft, then location tie-break
  transformed.sort((a, b) => {
    const severityA = SEVERITY_ORDER.indexOf(a.severityBucket);
    const severityB = SEVERITY_ORDER.indexOf(b.severityBucket);
    if (severityA !== severityB) {
      return severityA - severityB;
    }
    if (a.daysLeft !== b.daysLeft) {
      return a.daysLeft - b.daysLeft;
    }
    return a.tieBreakValue - b.tieBreakValue;
  });

  // 3) Add rank to each item (1-based index)
  const rankedTransformed = transformed.map((item, index) => ({
    ...item,
    rank: index + 1
  }));

  // 4) Build a summary array and log it
  const summary = rankedTransformed.map((item) => ({
    priority: item.priority,
    taskType: item.taskType,
    daysPassed: item.daysSinceOpen,
    severityBucket: item.severityBucket,
  }));

  return rankedTransformed;
}

// Export the function
export {
  rankInspectionRequests,
};





// /***************************************************************
//  * sortInspections.js
//  *
//  * Ranks NYC DOB inspection requests by urgency and returns
//  * a sorted array (high priority first).
//  ***************************************************************/

// /**
//  * SLA lookup for priority + taskType (Inspection vs. Re-Inspection).
//  *
//  * Priority + TaskType => SLA days from the Record Open Date
//  */
// const SLA_LOOKUP = {
//     'B': {
//       inspection: 40,
//       reinspection: 150,
//     },
//     'B+': {
//       inspection: 40,
//       reinspection: 150,
//     },
//     'C': {
//       inspection: 60,
//       reinspection: 175,
//     },
//     'D': {
//       inspection: 90,
//       reinspection: 200,
//     },
//   };
  
//   /**
//    * Helper: compute integer day difference between two dates.
//    * If dateA >= dateB, the result is how many whole days dateA is after dateB.
//    */
//   function daysBetween(dateA, dateB) {
//     const msPerDay = 24 * 60 * 60 * 1000;
//     // e.g.  If dateA is today, dateB is the record open date,
//     //       we get how many days have passed since open date.
//     return Math.floor((dateA - dateB) / msPerDay);
//   }
  
//   /**
//    * Returns the SLA (days) for a given priority & taskType
//    */
//   function getSlaDays(priority, taskType) {
//     const p = (priority || '').toUpperCase();    // e.g. 'B', 'B+', 'C', 'D'
//     const t = (taskType || '').toLowerCase();    // 'inspection' or 'reinspection'
  
//     if (SLA_LOOKUP[p] && SLA_LOOKUP[p][t] != null) {
//       return SLA_LOOKUP[p][t];
//     }
//     return 0; // fallback
//   }
  
//   /**
//    * rankInspectionRequests
//    *
//    * - Determines daysLeft = SLA - daysSinceOpen
//    * - Sorts ascending by daysLeft (lowest => highest priority)
//    * - Optionally tie-breaks by location.
//    * - Logs a summary array (priority, taskType, daysPassed) in sorted order.
//    *
//    * @param {Array} data - The array of objects read from XLSX, each like:
//    *   {
//    *     "INSPECTION_ID": "5826025",
//    *     "Record_Open_Date": "2023-08-18 00:00:00",
//    *     "Priority": "B",
//    *     "TaskType": "Complaint Re-Inspection - QOL",
//    *     "Status": "Scheduled",
//    *     "Latitude": "40.643533",
//    *     "Longitude": "-74.084529",
//    *     ...
//    *   }
//    * @param {Object} options
//    *   - locationTieBreak?: boolean
//    *   - referenceLat?: number
//    *   - referenceLng?: number
//    *
//    * @return {Array} sorted from high urgency (fewest daysLeft) to lower
//    */
//   function rankInspectionRequests(data, options = {}) {
//     const {
//       locationTieBreak = false,
//       referenceLat = 0,
//       referenceLng = 0,
//     } = options;
  
//     const today = new Date();
  
//     // 1) Transform and filter
//     const transformed = data
//       // .filter((row) => {
//       //   // If row.Status === "Scheduled", skip it
//       //   return row.Status !== 'Scheduled';
//       // })
//       .map((row) => {
//         // parse open date
//         const openDate = new Date(row.Record_Open_Date);
  
//         // how many days since the record was opened?
//         const daysSinceOpen = daysBetween(today, openDate);
  
//         // parse priority
//         const priority = String(row.Priority || '').toUpperCase();
  
//         // parse task type: check if "TaskType" string has "re-inspection"
//         let taskType = 'inspection';
//         const taskTypeStr = (row.TaskType || '').toLowerCase();
//         if (taskTypeStr.includes('re-inspection')) {
//           taskType = 'reinspection';
//         }
  
//         // get SLA
//         const slaDays = getSlaDays(priority, taskType);
  
//         // daysLeft = slaDays - daysSinceOpen
//         const daysLeft = slaDays - daysSinceOpen;
  
//         // parse lat/lng
//         const lat = parseFloat(row.Latitude);
//         const lng = parseFloat(row.Longitude);
  
//         // location-based tie-break
//         let tieBreakValue = 0;
//         if (locationTieBreak && !Number.isNaN(lat) && !Number.isNaN(lng)) {
//           // distance from reference
//           const dx = lng - referenceLng;
//           const dy = lat - referenceLat;
//           tieBreakValue = Math.sqrt(dx * dx + dy * dy);
//         }
  
//         return {
//           ...row,
//           openDate,
//           daysSinceOpen, // how many days have elapsed since open
//           slaDays,
//           daysLeft,
//           lat,
//           lng,
//           taskType,  // 'inspection' or 'reinspection'
//           priority,  // normalized e.g. 'B'
//           tieBreakValue
//         };
//       });
  
//     // 2) Sort by daysLeft ascending => smaller => more urgent
//     transformed.sort((a, b) => {
//       if (a.daysLeft !== b.daysLeft) {
//         return a.daysLeft - b.daysLeft; // ascending
//       }
//       // optional tie-break by location distance
//       return a.tieBreakValue - b.tieBreakValue;
//     });
  
//     // 3) Add rank to each item (1-based index)
//     const rankedTransformed = transformed.map((item, index) => ({
//       ...item,
//       rank: index + 1
//     }));

//     // 4) Build a summary array and log it
//     const summary = rankedTransformed.map((item) => ({
//       priority: item.priority,
//       taskType: item.taskType,
//       daysPassed: item.daysSinceOpen,
//     }));
  
//     return rankedTransformed;
//   }
  
//   // Export the function
//   export {
//     rankInspectionRequests,
//   };